import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { EventCard } from "@/components/shared/EventCard";
import { EventVideo } from "@/components/events/EventVideo";
import { fetchEvent } from "@/lib/api";
import { cn } from "@/lib/utils";

const EmbeddedEvent = ({ id }) => {
  const { data, isError } = useQuery({ queryKey: ["event", id], queryFn: () => fetchEvent(id), retry: false, staleTime: 60_000 });
  if (isError || !data) return null;
  return <div className="not-prose max-w-md" data-testid={`article-event-${id}`}><EventCard event={data} /></div>;
};

/**
 * Renders sanitised article HTML and brings the block markers to life: <div data-type="event"> becomes a live EventCard,
 * <div data-type="video"> a click-to-play video. Everything else is plain, beautifully typeset HTML.
 */
export const ArticleBody = ({ html, className, testId = "article-body" }) => {
  const ref = useRef(null);
  const [slots, setSlots] = useState([]);
  const inner = useMemo(() => ({ __html: html || "" }), [html]);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const found = [...root.querySelectorAll('[data-type="event"][data-id], [data-type="video"][data-url]')].map((el) => {
      el.innerHTML = "";
      return { el, type: el.dataset.type, id: el.dataset.id, url: el.dataset.url };
    });
    setSlots(found);
  }, [html]);
  return (
    <>
      <div ref={ref} className={cn("article-body", className)} data-testid={testId} dangerouslySetInnerHTML={inner} />
      {slots.map((s, i) => createPortal(s.type === "event" ? <EmbeddedEvent id={s.id} /> : <EventVideo url={s.url} title="Video" />, s.el, `${s.type}-${s.id || s.url}-${i}`))}
    </>
  );
};
